import { useState, useRef, useEffect } from 'react';
import { Download, ChevronDown } from 'lucide-react';
import type { Image } from '../../types/image';
import { getDownloadOptions } from '../../lib/download';
import type { DownloadSize } from '../../lib/download';

export interface DownloadOptionsProps {
  image: Image;
  defaultSize?: DownloadSize;
  variant?: 'icon' | 'button';
}

export const DownloadOptions: React.FC<DownloadOptionsProps> = ({
  image,
  defaultSize = 'original',
  variant = 'button',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const options = getDownloadOptions(image);
  const defaultOption = options.find((o) => o.key === defaultSize) || options[0];

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (options.length === 0) {
    return null;
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const handleOptionClick = () => {
    setIsOpen(false);
  };

  if (variant === 'icon') {
    return (
      <div className="relative" ref={containerRef}>
        <button
          onClick={handleToggle}
          className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Download image"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          title="Download"
        >
          <Download className="w-5 h-5" />
        </button>

        {isOpen && (
          <div
            className="absolute right-0 top-full mt-2 w-56 bg-black/90 backdrop-blur-sm rounded-lg shadow-lg border border-white/10 py-1 z-50"
            role="menu"
            aria-label="Download options"
          >
            {options.map((option) => (
              <a
                key={option.key}
                href={option.url}
                download={image.filename}
                onClick={handleOptionClick}
                className="block px-4 py-2 text-sm text-white/90 hover:bg-white/10 transition-colors"
                role="menuitem"
              >
                {option.label}
              </a>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative inline-flex" ref={containerRef}>
      <a
        href={defaultOption?.url}
        download={image.filename}
        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-l-lg hover:bg-blue-700 transition-colors font-medium"
      >
        <Download className="w-4 h-4" />
        Download {defaultOption ? defaultOption.label.split(' ')[0] : 'Original'}
      </a>
      <button
        onClick={handleToggle}
        className="inline-flex items-center px-2 py-2 bg-blue-600 text-white rounded-r-lg border-l border-blue-500 hover:bg-blue-700 transition-colors"
        aria-label="Show download options"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          role="menu"
          aria-label="Download options"
        >
          {options.map((option) => (
            <a
              key={option.key}
              href={option.url}
              download={image.filename}
              onClick={handleOptionClick}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              role="menuitem"
            >
              {option.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
